﻿import {createFromNumbers, dateCompare, dateEquals, getLastOfMonth, throwsIfInvalidDate} from "./DateTimeHelpers";
import {DayOfWeek} from "./DayOfWeek";

function toISOString(date: Date): string {
	return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds())).toISOString().slice(0, 10);
}

export class DateOnly {
	private static _minValue: DateOnly | null = null;
	private static _maxValue: DateOnly | null = null;
	private readonly _date: Date;

	private constructor(date: Date, isUtc: boolean = false) {
		this._date = isUtc ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0) : new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0);
		throwsIfInvalidDate(this._date, "DateOnly");
	}

	get [Symbol.toStringTag](): string {
		return "DateOnly";
	}

	/**
	 * Creates a DateOnly from the current date
	 */
	static today(): DateOnly {
		return new DateOnly(new Date());
	}

	/**
	 * Creates a DateOnly from a Date object
	 */
	static fromDate(date: Date, isUtc: boolean = false): DateOnly {
		return new DateOnly(date, isUtc);
	}

	/**
	 * Creates a DateOnly from a timestamp (milliseconds since epoch)
	 */
	static fromTimestamp(timestamp: number): DateOnly {
		return new DateOnly(new Date(timestamp));
	}

	/**
	 * Creates a DateOnly from a date string (ISO format or other format accepted by Date constructor)
	 */
	static fromIsoString(dateString: string): DateOnly {
		if (dateString.includes("T")) {
			dateString = dateString.split("T")[0];
		}

		return new DateOnly(new Date(dateString + "T00:00:00Z"));
	}

	/**
	 * Creates a DateOnly from year, month, and day values
	 * @param year The full year
	 * @param month The month (1-12)
	 * @param day The day of month (1-31)
	 */
	static fromComponents(year: number, month: number = 1, day: number = 1): DateOnly {
		return new DateOnly(createFromNumbers(year, month, day));
	}

	static minValue(): DateOnly {
		return this._minValue ??= DateOnly.fromTimestamp(0);
	}

	static maxValue(): DateOnly {
		return this._maxValue ??= DateOnly.fromTimestamp(8640000000000000);
	}

	getYear(): number {
		return this._date.getFullYear();
	}

	/**
	 * Returns an integer, between 1 and 12, representing the month for the given date according to local time.
	 */
	getMonth(): number {
		return this._date.getMonth() + 1;
	}

	/**
	 * Returns an integer, between 1 and 31, representing the day of the month for the given date according to local time.
	 * @return {number} The day of the month as a number.
	 */
	getDay(): number {
		return this._date.getDate();
	}

	getDayOfWeek(): DayOfWeek {
		return this._date.getDay();
	}

	getUtcDate(): Date {
		return this._date;
	}

	getLastOfMonth(): number {
		return getLastOfMonth(this._date);
	}

	equals(other: DateOnly | null | undefined): boolean {
		return dateEquals(this._date, other?._date);
	}

	compareTo(other: DateOnly | null | undefined): number {
		return dateCompare(this._date, other?._date);
	}

	addDays(days: number): DateOnly {
		const newDate = new Date(this._date);
		newDate.setDate(newDate.getDate() + days);
		return new DateOnly(newDate, true);
	}

	addWeeks(weeks: number): DateOnly {
		return this.addDays(weeks * 7);
	}

	addMonths(months: number): DateOnly {
		const newDate = new Date(this._date);
		newDate.setMonth(newDate.getMonth() + months);
		return new DateOnly(newDate, true);
	}

	addYears(years: number): DateOnly {
		const newDate = new Date(this._date);
		newDate.setFullYear(newDate.getFullYear() + years);
		return new DateOnly(newDate, true);
	}

	toString(): string {
		return toISOString(this._date);
	}

	[Symbol.toPrimitive](hint: string): number | string {
		if (hint === "number") {
			return this._date.getTime();
		}

		return toISOString(this._date);
	}

	toJSON(): string {
		return toISOString(this._date);
	}
}
